// Shared pieces of the procurement screens (docs/plans/phase2-procurement.md §5).
import { Link, useLocation } from '@tanstack/react-router';
import { Button, Dialog, DialogActions, Notice, TextArea } from '@wodi/ui';
import { useState } from 'react';

import { useOnline } from '../hooks/use-online.ts';
import { screen } from '../layout/screen.tsx';
import { errorText } from '../stock/common.tsx';

/** The views of the procurement area, as links styled like view tabs (ADR 0019). */
export function ProcurementTabs() {
  const location = useLocation();
  const links = [
    { to: '/procurement', label: 'LPOs' },
    { to: '/procurement/requisitions', label: 'Purchase requisitions' },
    { to: '/procurement/invoices', label: 'Supplier invoices' },
    { to: '/procurement/suppliers', label: 'Supplier statements' },
  ] as const;
  return (
    <nav aria-label="Procurement" className={screen.tabs}>
      {links.map((l) => (
        <Link
          key={l.to}
          to={l.to}
          activeOptions={{ exact: true }}
          {...(location.pathname === l.to ? { 'aria-current': 'page' as const } : {})}
        >
          {l.label}
        </Link>
      ))}
    </nav>
  );
}

/** A query failure with what happened and a way to try again. */
export function LoadFailed({
  error,
  fallback,
  onRetry,
}: {
  error: unknown;
  fallback: string;
  onRetry: () => void;
}) {
  return (
    <Notice
      tone="error"
      action={
        <Button dense onPress={onRetry}>
          Try again
        </Button>
      }
    >
      {errorText(error, fallback)}
    </Notice>
  );
}

/** Shown beside write actions while the device is offline. */
export function OfflineNote({ what }: { what: string }) {
  const online = useOnline();
  if (online) return null;
  return (
    <Notice tone="info">{`${what} needs a connection to Wodi. Reconnect, then try again.`}</Notice>
  );
}

/**
 * Asks for a reason, then runs the decision. The reason is kept with the decision and
 * audited (CLAUDE.md, domain rule 10). `optional` lets a clean invoice be approved without one.
 */
export function ReasonDialog({
  title,
  confirmLabel,
  pendingLabel = 'Saving…',
  description = 'At least five characters. It is kept with the decision.',
  optional = false,
  children,
  isPending,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  confirmLabel: string;
  pendingLabel?: string;
  description?: string;
  optional?: boolean;
  children?: React.ReactNode;
  isPending: boolean;
  error: unknown;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const online = useOnline();
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const ready = optional ? trimmed === '' || trimmed.length >= 5 : trimmed.length >= 5;
  return (
    <Dialog
      title={title}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {children}
      <TextArea
        label={optional ? 'Reason (optional)' : 'Reason'}
        description={description}
        value={reason}
        onChange={setReason}
        autoFocus
      />
      {error === null || error === undefined ? null : (
        <Notice tone="error">{errorText(error, "That wasn't saved. Try again.")}</Notice>
      )}
      {online ? null : <Notice tone="info">This needs a connection to Wodi.</Notice>}
      <DialogActions>
        <Button
          variant="primary"
          isDisabled={!ready || !online}
          isPending={isPending}
          pendingLabel={pendingLabel}
          onPress={() => {
            onConfirm(trimmed);
          }}
        >
          {confirmLabel}
        </Button>
        <Button variant="quiet" onPress={onClose}>
          Back
        </Button>
      </DialogActions>
    </Dialog>
  );
}
